import { createCssCompNode } from '../cssCompNode'
import type { FeatureRefs } from '../cssFeatures.types'
import type { SelectorObj } from '../../shared.types'
import type { PinnedChild, PinnedStyle } from './pinnedLayout.types'
import type { AbsoluteLayout, Component, TPAGluedWidgetProperties } from '@wix/thunderbolt-becky-types'
import type { DockedCompLayout } from '@wix/thunderbolt-becky-root'
import { getDockedStyles } from '@wix/thunderbolt-becky-root'
import { kebabCase, mapKeys, mapValues } from 'lodash'
import { fixTPAGluedWidgetLayout } from './TPAGluedWidgetLayoutFixer'
import { getRegularIdSelector } from '../../utils/selectorsUtils'
import { envRefs } from '@wix/thunderbolt-catharsis'
import { isInflatedId } from '@wix/thunderbolt-commons'

const PAGE_COMPS = ['MasterPage', 'Page', 'PopupContainer', 'AppPage']

const isPinned = (compLayout: AbsoluteLayout) => compLayout && compLayout.fixedPosition && !!compLayout.docked

const isDockedCompLayout = (pinnedStyle: DockedCompLayout): pinnedStyle is DockedCompLayout => {
	return !!pinnedStyle && 'id' in pinnedStyle
}

const computeDockedStyles = (childrenDockedLayout: Array<PinnedChild>): SelectorObj | null => {
	const idToAutoHeightMapper: Record<string, boolean> = {}
	const dockedLayouts: Array<DockedCompLayout> = []
	// Going over all the children of the pinned component and collecting the docked components
	// and creating a map of the id to autoHeightWrapper
	childrenDockedLayout.forEach((childComp) => {
		if (childComp?.layout) {
			if (isDockedCompLayout(childComp.layout)) {
				dockedLayouts.push(childComp.layout)
			}
			idToAutoHeightMapper[childComp?.layout?.id] = childComp.autoHeightWrapper || false
		}
	})

	if (!dockedLayouts.length) {
		return null
	}

	const pinnedComponentsStylesMap = getDockedStyles(dockedLayouts, false, false)
	const pinnedComponentsCssMap = mapValues(pinnedComponentsStylesMap, (cssObj) =>
		mapKeys(cssObj, (__, key) => kebabCase(key))
	)
	return mapKeys(pinnedComponentsCssMap, (__, compId) => {
		return getRegularIdSelector(idToAutoHeightMapper[compId] ? `wrapper-${compId}` : compId)
	})
}

const getDockedLayout = (compId: string, compLayout: AbsoluteLayout): DockedCompLayout | null => {
	if (!isPinned(compLayout)) {
		return null
	}

	const { x: left, y: top, width, height, docked = {}, fixedPosition, rotationInDegrees } = compLayout

	return {
		id: compId,
		left,
		top,
		width,
		height,
		docked,
		isFixed: fixedPosition,
		rotationInDegrees,
	}
}

export const pinnedStyle = createCssCompNode('pinnedLayout', 'pinnedStyle', {
	getDependencies: (component: Component, refs: FeatureRefs<'pinnedLayout'>) => {
		// only direct children of Page or PopupContainer can be pinned components

		const isPageComp = PAGE_COMPS.includes(component.componentType)
		if (isPageComp) {
			return {
				childrenDockedLayout: (component.components || []).map((childComp) => {
					return {
						layout: refs.pinnedStyle(childComp),
						autoHeightWrapper: refs.autoHeightWrapper?.(childComp),
					}
				}),
			}
		}

		return {
			compProps: refs.propertyQuery<TPAGluedWidgetProperties>(component.propertyQuery!),
			autoHeightWrapper: refs.autoHeightWrapper?.(component),
			isResponsive: envRefs.isResponsive,
			experiments: envRefs.experiments,
		}
	},
	toViewItem: (component: Component, deps): PinnedStyle => {
		// only direct children of Page or PopupContainer can have pinned style
		if (deps?.childrenDockedLayout) {
			const selectorToCssMap = computeDockedStyles(deps.childrenDockedLayout as Array<PinnedChild>)
			return selectorToCssMap
				? {
					type: 'SelectorObj',
					selectorObj: selectorToCssMap,
				}
				: null
		}
		const compLayout =
			component.componentType === 'TPAGluedWidget'
				? fixTPAGluedWidgetLayout(component.layout, deps.compProps)
				: component.layout

		// Wix AI Chat should apply the component's container CSS to the RefComponent to align the chat to the right.
		if (
			deps.experiments['specs.thunderbolt.applyCssOnRefcompTpawidgetParent'] &&
			deps.isResponsive &&
			component.componentType === 'TPAGluedWidget' &&
			isInflatedId(component.id)
		) {
			const selectorToCssMap = computeDockedStyles([
				{
					layout: { ...compLayout, id: component.parent } as any,
					autoHeightWrapper: deps.autoHeightWrapper,
					compProps: deps.compProps,
				},
			])
			return selectorToCssMap
				? {
					type: 'SelectorObj',
					selectorObj: selectorToCssMap,
				}
				: null
		}
		return getDockedLayout(component.id, compLayout as AbsoluteLayout)
	},
})
